import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { TGlobalStores } from '@/editor/stores';
import { EDirection, EOperateMode, ESceneMode, IOperateInfo } from '@/editor/types';
import { DrawFurniture } from './controller2d/drawFurniture';
import DrawSeparateWall from './controller2d/drawSeparateWall';
import { DrawStruct } from './controller2d/drawStruct';
import DrawWall from './controller2d/drawWall';
import { DrawRectWall } from './controller2d/drawRectWall';
import { DrawWindow } from './controller2d/drawWindow';
import { OperateEntityController } from './controller2d/operateEntityController';
import { AbstractCommonController } from './AbstractCommonController';
import { MultiSelect3d, OperateEntityController3d } from './controller3d';
import DrawArcWall from './controller2d/drawArcWall';
import { OperateFloorplanController } from './controller2d/operateFloorplan/operateFloorplan';
import { OperateLayoutController } from './controller2d/operateLayout/operateLayout';
import { DrawAssembly } from './controller2d/drawAssembly';
import { DrawMultiModel } from './controller2d/drawMultiModel';
import MultiSelect from './controller2d/multiSelect';
import { OperateArrayController } from './controller2d/operateArray/operateArray';
import { OperateAlignment } from './controller2d/operateAlignment/operateAlignment';
import { OperateMeasure } from './controller2d/operateMeasure/operateMeasure';
import { DrawAreaDimension } from './controller2d/drawAreaDimension';
import { DrawLinearDimension } from './controller2d/drawLinearDimension';
import DrawArcSeparateWall from './controller2d/drawArcSeparateWall';

export class ControllerManager {
  private _controller: AbstractCommonController | null = null;

  constructor(private _gs: TGlobalStores) {
    this.handleOperateModeChange();

    editorEvent.on(EditorEventName.OPERATE_MODE_CHANGE, this.handleOperateModeChange);
    editorEvent.on(EditorEventName.SCENE_MODE_CHANGE, this.handleOperateModeChange);
    editorEvent.on(EditorEventName.CONTROLLER_REFRESH, this.handleOperateModeChange);
  }

  private createController(sceneMode: ESceneMode, operateInfo: IOperateInfo) {
    switch (sceneMode) {
      case ESceneMode.scene2d:
        return this._create2dController(operateInfo);
      case ESceneMode.scene3d:
        return this._create3dController(operateInfo);
      default:
        console.warn('该 sceneMode 还没实现对应的控制器', sceneMode);
    }

    return this._create2dController(operateInfo);
  }

  private _create2dController(operateInfo: IOperateInfo) {
    switch (operateInfo.mode) {
      case EOperateMode.DrawWall:
        return new DrawWall(this._gs);
      case EOperateMode.DrawRectWall:
        return new DrawRectWall(this._gs);
      case EOperateMode.DrawArcWall:
        return new DrawArcWall(this._gs);
      case EOperateMode.DrawFurniture:
        return new DrawFurniture(this._gs);
      case EOperateMode.DrawAssembly:
        return new DrawAssembly(this._gs);
      case EOperateMode.DrawMultiModel:
        return new DrawMultiModel(this._gs);
      case EOperateMode.DrawSeparateWall:
        return new DrawSeparateWall(this._gs);
      case EOperateMode.DrawArcSeparateWall:
        return new DrawArcSeparateWall(this._gs);
      case EOperateMode.DrawStruct:
        return new DrawStruct(this._gs);
      case EOperateMode.DrawDoorWindow:
        return new DrawWindow(this._gs);
      case EOperateMode.DrawAreaDimension:
        return new DrawAreaDimension(this._gs);
      case EOperateMode.DrawLinearDimension:
        return new DrawLinearDimension(this._gs);
      case EOperateMode.Array:
        return new OperateArrayController(this._gs);
      case EOperateMode.Floorplan:
        return new OperateFloorplanController(this._gs);
      case EOperateMode.Layout:
        return new OperateLayoutController(this._gs);
      case EOperateMode.Alignment:
        return new OperateAlignment(this._gs);
      case EOperateMode.EMPTY:
        return new OperateEntityController(this._gs);
      case EOperateMode.MultiSelect:
        return new MultiSelect(this._gs);
      case EOperateMode.MeasureRuler:
        return new OperateMeasure(this._gs);
      default:
        console.warn('[2D] 该操作模式还没实现对应的控制器', operateInfo.mode);
    }
    return null;
  }

  private _create3dController(operateInfo: IOperateInfo) {
    switch (operateInfo.mode) {
      case EOperateMode.EMPTY:
        return new OperateEntityController3d(this._gs);
      case EOperateMode.MultiSelect:
        return new MultiSelect3d(this._gs);
      default:
        console.warn('[3D] 该操作模式还没实现对应的控制器', operateInfo.mode);
    }
    return null;
  }

  fitToView() {
    if (!this._controller) return;

    this._controller.fitToView();
  }

  keyboardMoveUpdate() {
    if (!this._controller) return;

    this._controller.keyboardMoveUpdate();
  }

  public moveEntityByDirection(direction: EDirection) {
    if (!this._controller) return;

    this._controller.moveEntityByDirection(direction);
  }

  public flushSync() {
    if (!this._controller) return;

    this._controller.flushSync();
  }

  handleOperateModeChange = () => {
    const currentOperateInfo = this._gs.operateInfo;
    const currentSceneMode = this._gs.sceneMode;

    // 销毁当前控制器
    if (this._controller) {
      this._controller.dispose();
    }

    // 创建新的控制器
    this._controller = this.createController(currentSceneMode, currentOperateInfo);
    if (this._controller) this._controller.init();
  };

  dispose() {
    editorEvent.off(EditorEventName.SCENE_MODE_CHANGE, this.handleOperateModeChange);
    editorEvent.off(EditorEventName.OPERATE_MODE_CHANGE, this.handleOperateModeChange);
    editorEvent.off(EditorEventName.CONTROLLER_REFRESH, this.handleOperateModeChange);

    if (this._controller) {
      this._controller.dispose();
      this._controller = null;
    }
  }
}
